import {
  createRouter,
  createRoute,
  createRootRoute,
  Outlet,
  redirect,
} from '@tanstack/react-router';

import { useAuthStore } from '@/stores/auth';
import { AppShell } from '@/layouts/AppShell';
import { AuthLayout } from '@/layouts/AuthLayout';
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { DiveLogsListPage } from '@/pages/dive-logs/DiveLogsListPage';
import { DiveLogDetailPage } from '@/pages/dive-logs/DiveLogDetailPage';
import { DiveLogCreatePage } from '@/pages/dive-logs/DiveLogCreatePage';
import { DiveSitesListPage } from '@/pages/dive-sites/DiveSitesListPage';
import { DiveSiteDetailPage } from '@/pages/dive-sites/DiveSiteDetailPage';
import { AccountPage } from '@/pages/AccountPage';
import { WelcomePage } from '@/pages/WelcomePage';

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// Public landing page ─────────────────────────────────────────────────────
const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/welcome',
  component: WelcomePage,
});

// Auth (public) routes ────────────────────────────────────────────────────
const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth-layout',
  component: AuthLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/login',
  component: LoginPage,
});

const registerRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/register',
  component: RegisterPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/reset-password/$token',
  component: ResetPasswordPage,
});

// Protected app routes ────────────────────────────────────────────────────
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app-layout',
  beforeLoad: () => {
    if (!useAuthStore.getState().isAuthenticated) {
      // Send unauthenticated visitors to the marketing landing first;
      // the login/register CTAs live there.
      throw redirect({ to: '/welcome' });
    }
  },
  component: AppShell,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  component: DashboardPage,
});

const diveLogsListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/logs',
  component: DiveLogsListPage,
});

const diveLogCreateRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/logs/new',
  component: DiveLogCreatePage,
});

const diveLogDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/logs/$id',
  component: DiveLogDetailPage,
});

const diveSitesListRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/sites',
  component: DiveSitesListPage,
});

const diveSiteDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/sites/$id',
  component: DiveSiteDetailPage,
});

const accountRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/account',
  component: AccountPage,
});

const routeTree = rootRoute.addChildren([
  welcomeRoute,
  authLayoutRoute.addChildren([loginRoute, registerRoute, forgotPasswordRoute, resetPasswordRoute]),
  appLayoutRoute.addChildren([
    dashboardRoute,
    diveLogsListRoute,
    diveLogCreateRoute,
    diveLogDetailRoute,
    diveSitesListRoute,
    diveSiteDetailRoute,
    accountRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
